import { playerHubApi } from './chatApi';

interface PublicKeyResponse {
    publicKey: string;
}

export const fetchWebPushPublicKey = async (): Promise<string> => {
    const response = await playerHubApi.get<PublicKeyResponse>('/webpush/public-key');
    return response.data.publicKey;
};

export const subscribeWebPush = async (subscription: PushSubscriptionJSON): Promise<void> => {
    await playerHubApi.post('/webpush/subscribe', subscription);
};

export const unsubscribeWebPush = async (endpoint: string): Promise<void> => {
    await playerHubApi.delete('/webpush/subscribe', { data: { endpoint, keys: { p256dh: '', auth: '' } } });
};

// Converts a base64-urlsafe VAPID key (as returned by the backend) into the
// Uint8Array form required by PushManager.subscribe().
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const out = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        out[i] = rawData.charCodeAt(i);
    }
    return out;
};

/**
 * Register the service worker, subscribe to the push service, and POST the
 * subscription to player-hub. Safe to call repeatedly — re-uses the existing
 * subscription if the user has already subscribed.
 */
export const ensurePushSubscription = async (): Promise<void> => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Web Push not supported by this browser');
        return;
    }

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
        const publicKey = await fetchWebPushPublicKey();
        if (!publicKey) {
            console.error('Cannot subscribe: backend returned empty VAPID public key');
            return;
        }
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }

    await subscribeWebPush(subscription.toJSON());
};

/**
 * Register the service worker without subscribing. Useful at app boot so the
 * SW is installed before the user clicks Enable.
 */
export const registerServiceWorker = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) return;
    try {
        await navigator.serviceWorker.register('/sw.js');
    } catch (err) {
        console.error('Failed to register service worker:', err);
    }
};
